/**
 * Game Test Runner
 * Automated integration testing for Helix 2000
 * Can run in browser or headless mode
 */

import { gameLogger } from '../../utils/GameLogger';

export interface TestResult {
  name: string;
  passed: boolean;
  duration: number;
  error?: string;
  details?: any;
}

export interface TestScenario {
  name: string;
  description: string;
  run: () => Promise<TestResult>;
}

export type TestHook = () => Promise<void>;

export interface TestSuite {
  name: string;
  scenarios: TestScenario[];
  beforeAll?: TestHook;
  afterAll?: TestHook;
  beforeEach?: TestHook;
  afterEach?: TestHook;
}

export class TestAssertionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TestAssertionError';
  }
}

export class GameTestRunner {
  private scenarios: TestScenario[] = [];
  private beforeEachHook: TestHook | null = null;
  private afterEachHook: TestHook | null = null;
  private isRunning = false;
  private results: TestResult[] = [];

  /**
   * Register a test scenario
   */
  addScenario(scenario: TestScenario): void {
    // Name is the runner's lookup key (runScenario resolves by name), so
    // re-registering a name replaces the entry instead of duplicating it —
    // registration sites run both at module init and on window mount.
    const at = this.scenarios.findIndex(s => s.name === scenario.name);
    if (at >= 0) {
      this.scenarios[at] = scenario;
      return;
    }
    this.scenarios.push(scenario);
    gameLogger.info('test', `Registered test scenario: ${scenario.name}`);
  }

  /**
   * Set hook to run before each test
   */
  beforeEach(hook: TestHook): void {
    this.beforeEachHook = hook;
  }

  /**
   * Set hook to run after each test
   */
  afterEach(hook: TestHook): void {
    this.afterEachHook = hook;
  }

  /**
   * Run all registered test scenarios
   */
  async runAll(options: {
    stopOnFailure?: boolean;
    verbose?: boolean;
  } = {}): Promise<TestResult[]> {
    const { stopOnFailure = false, verbose = true } = options;

    if (this.isRunning) {
      throw new Error('Test runner is already running');
    }

    this.isRunning = true;
    this.results = [];

    gameLogger.info('test', `🧪 Starting ${this.scenarios.length} test scenarios...`);
    console.group('🧪 Game Tests');

    for (const scenario of this.scenarios) {
      const result = await this.runScenarioImpl(scenario, verbose);
      this.results.push(result);

      if (!result.passed && stopOnFailure) {
        gameLogger.error('test', `Stopping on failure: ${result.name}`);
        break;
      }
    }

    console.groupEnd();
    this.isRunning = false;

    // Print summary
    this.printSummary();

    return this.results;
  }



  /**
   * Print test summary
   */
  private printSummary(): void {
    const passed = this.results.filter(r => r.passed).length;
    const failed = this.results.filter(r => !r.passed).length;
    const total = this.results.length;
    const totalDuration = this.results.reduce((sum, r) => sum + r.duration, 0);

    gameLogger.info('test', '');
    gameLogger.info('test', '📊 Test Summary');
    gameLogger.info('test', '================');
    gameLogger.info('test', `Total: ${total} tests`);
    gameLogger.info('test', `✅ Passed: ${passed}`);
    gameLogger.info('test', `❌ Failed: ${failed}`);
    gameLogger.info('test', `⏱️ Duration: ${totalDuration.toFixed(2)}ms`);
    gameLogger.info('test', `Success Rate: ${((passed / total) * 100).toFixed(1)}%`);

    if (failed > 0) {
      gameLogger.info('test', '');
      gameLogger.info('test', 'Failed Tests:');
      this.results
        .filter(r => !r.passed)
        .forEach(r => gameLogger.error('test', `  ❌ ${r.name}: ${r.error}`));
    }
  }

  /**
   * Get test results
   */
  getResults(): TestResult[] {
    return [...this.results];
  }

  /**
   * Clear all registered scenarios
   */
  clear(): void {
    this.scenarios = [];
    this.results = [];
    gameLogger.info('test', 'Cleared all test scenarios');
  }

  /**
   * Check if tests are currently running
   */
  getIsRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Get list of available scenario names
   */
  getAvailableScenarios(): string[] {
    return this.scenarios.map(s => s.name);
  }

  /**
   * Run a scenario by name
   */
  async runScenario(name: string, verbose: boolean = true): Promise<TestResult> {
    const scenario = this.scenarios.find(s => s.name === name);
    if (!scenario) {
      throw new Error(`Scenario '${name}' not found`);
    }
    return this.runScenarioImpl(scenario, verbose);
  }

  /**
   * Run all scenarios
   */
  async runAllScenarios(): Promise<boolean> {
    const results = await this.runAll();
    return results.every(r => r.passed);
  }

  /**
   * Internal implementation of runScenario
   */
  private async runScenarioImpl(scenario: TestScenario, verbose: boolean): Promise<TestResult> {
    if (verbose) {
      console.group(`▶️ ${scenario.name}`);
      gameLogger.info('test', `   ${scenario.description}`);
    }

    const startTime = performance.now();

    try {
      // Run before hook
      if (this.beforeEachHook) {
        await this.beforeEachHook();
      }

      // Run the test
      const result = await scenario.run();
      result.duration = performance.now() - startTime;

      // Run after hook
      if (this.afterEachHook) {
        await this.afterEachHook();
      }

      if (verbose) {
        if (result.passed) {
          gameLogger.info('test', `✅ PASSED (${result.duration.toFixed(2)}ms)`);
        } else {
          gameLogger.error('test', `FAILED: ${result.error}`);
        }
        console.groupEnd();
      }

      return result;

    } catch (error) {
      const duration = performance.now() - startTime;
      const result: TestResult = {
        name: scenario.name,
        passed: false,
        duration,
        error: error instanceof Error ? error.message : String(error)
      };

      if (verbose) {
        gameLogger.error('test', `FAILED: ${result.error}`);
        console.groupEnd();
      }

      return result;
    }
  }
}

// Singleton instance
let testRunner: GameTestRunner | null = null;

export function getTestRunner(): GameTestRunner {
  if (!testRunner) {
    testRunner = new GameTestRunner();
  }
  return testRunner;
}

export function resetTestRunner(): void {
  testRunner = null;
}

// Helper functions for writing tests
export function expect(actual: any) {
  return {
    toBe(expected: any): void {
      if (actual !== expected) {
        throw new Error(`Expected ${expected}, got ${actual}`);
      }
    },
    toEqual(expected: any): void {
      if (JSON.stringify(actual) !== JSON.stringify(expected)) {
        throw new Error(`Expected ${JSON.stringify(expected)}, got ${JSON.stringify(actual)}`);
      }
    },
    toBeDefined(): void {
      if (actual === undefined) {
        throw new Error('Expected value to be defined, got undefined');
      }
    },
    toBeNull(): void {
      if (actual !== null) {
        throw new Error(`Expected null, got ${actual}`);
      }
    },
    toBeGreaterThan(expected: number): void {
      if (!(actual > expected)) {
        throw new Error(`Expected ${actual} to be greater than ${expected}`);
      }
    },
    toBeLessThan(expected: number): void {
      if (!(actual < expected)) {
        throw new Error(`Expected ${actual} to be less than ${expected}`);
      }
    },
    toContain(expected: any): void {
      if (!actual?.includes?.(expected)) {
        throw new Error(`Expected ${actual} to contain ${expected}`);
      }
    },
    toBeTruthy(): void {
      if (!actual) {
        throw new Error(`Expected truthy value, got ${actual}`);
      }
    },
    toBeFalsy(): void {
      if (actual) {
        throw new Error(`Expected falsy value, got ${actual}`);
      }
    }
  };
}

// Async wait helper
export function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Assertion helper
export function assert(condition: boolean, message?: string): void {
  if (!condition) {
    throw new TestAssertionError(message || 'Assertion failed');
  }
}

export default GameTestRunner;
